import { Link } from "react-router-dom";
import {
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Award,
  Compass,
  HandHeart,
  ShieldCheck,
  Sparkles,
  Quote,
} from "lucide-react";
import { PolicyHeader } from "@/components/PolicyHeader";

const FOUNDER_PRINCIPLES = [
  {
    icon: ShieldCheck,
    title: "Integrity First",
    body: "Every decision passes the test of transparency, fairness and long-term customer protection — no shortcuts, no surprises.",
  },
  {
    icon: HandHeart,
    title: "Inclusive Finance",
    body: "Financial services should reach tribal, rural and underserved India first — not last. That conviction shapes our product design.",
  },
  {
    icon: Compass,
    title: "Long-Term Thinking",
    body: "Build relationships that last generations, not quarters. Our success is measured in customers retained — not just acquired.",
  },
  {
    icon: Sparkles,
    title: "Technology with Empathy",
    body: "Digital tools should amplify human judgement, not replace it. We pair AI-powered platforms with people who understand the community.",
  },
];

const FOUNDER_FOCUS = [
  "Project Finance & Infrastructure",
  "MSME & Business Funding",
  "Agriculture & Rural Lending",
  "Wealth Management & Investment Advisory",
  "Strategic Partnerships & Network Expansion",
  "Corporate Governance & Compliance",
  "Customer Experience & Operational Excellence",
  "Talent Development & Leadership Mentorship",
];

export default function Leadership() {
  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <PolicyHeader
        eyebrow="Our Leadership"
        title={
          <>
            Meet the people behind{" "}
            <em className="not-italic text-[#D4AF37]">Sahyog Finance</em> &amp;{" "}
            <span className="font-script italic text-[#F0D788]">Noble Infinity</span>
          </>
        }
        lead="Built by professionals who care deeply about how finance can change lives — combining decades of operational depth with a customer-first philosophy."
      />

      {/* Founder profile */}
      <section
        data-testid="leadership-founder"
        className="py-12 md:py-16 border-t border-white/5"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-[420px_1fr] gap-10 items-start">
          {/* Portrait */}
          <div className="relative mx-auto lg:mx-0">
            <span className="absolute -inset-5 rounded-full bg-gradient-to-br from-[#D4AF37]/40 via-transparent to-emerald-500/15 blur-3xl pointer-events-none" />
            <img
              src="/founder-digantkumar.jpg"
              alt="Digantkumar I. Gamit, Managing Director & Founder"
              data-testid="leadership-founder-photo"
              className="relative w-[280px] h-[280px] md:w-[360px] md:h-[360px] lg:w-[420px] lg:h-[420px] rounded-full object-cover ring-4 ring-[#D4AF37]/55 shadow-[0_28px_70px_rgba(0,0,0,0.6)]"
            />
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#D4AF37] text-[#0A1F44] text-xs font-bold uppercase tracking-[0.18em] shadow-lg whitespace-nowrap">
              Managing Director · Founder
            </div>
          </div>

          {/* Bio */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
              The Founder
            </div>
            <h2
              data-testid="leadership-founder-name"
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-tight"
            >
              Digantkumar I. Gamit
            </h2>
            <div className="mt-1 text-sm text-[#F0D788] uppercase tracking-[0.22em]">
              Managing Director &amp; Founder
            </div>

            <div className="mt-6 space-y-4 text-sm md:text-base text-white/80 leading-relaxed">
              <p>
                Digantkumar I. Gamit founded Sahyog Finance &amp; Noble Infinity Asset Management
                Private Limited with a clear conviction — that financial services in India must
                become more <strong className="text-white">transparent, accessible and
                customer-centric</strong>, especially for tribal communities, MSMEs, farmers,
                and aspiring entrepreneurs who have historically been underserved.
              </p>
              <p>
                Born and raised in Tapi district of Gujarat, Diganbhai brings a deep
                understanding of the cultural and economic fabric of rural &amp; tribal India.
                He has built Sahyog Finance from the ground up — combining ethical lending,
                modern technology, and a relentless focus on long-term relationships over
                short-term wins.
              </p>
              <p>
                Under his leadership, the organization has expanded into fourteen loan products,
                established a 6-level partner network, and laid the foundation for one of South
                Gujarat&apos;s most trusted integrated financial-services ecosystems —
                spanning lending, investment advisory, wealth management, corporate consulting,
                and compliance services.
              </p>
            </div>

            {/* Contact pills */}
            <div className="mt-7 flex flex-wrap gap-2">
              <a
                href="mailto:sahyogpfir@gmail.com"
                data-testid="leadership-founder-email"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-xs text-white/85 hover:border-[#D4AF37]/70"
              >
                <Mail size={12} className="text-[#D4AF37]" /> sahyogpfir@gmail.com
              </a>
              <a
                href="tel:+917016975324"
                data-testid="leadership-founder-phone"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-xs text-white/85 hover:border-[#D4AF37]/70"
              >
                <Phone size={12} className="text-[#D4AF37]" /> +91 70169 75324
              </a>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.02] text-xs text-white/65">
                <MapPin size={12} className="text-[#D4AF37]" /> Vyara, Tapi, Gujarat
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Founder principles */}
      <section
        data-testid="leadership-principles"
        className="py-14 md:py-18 border-t border-white/5"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            Principles He Leads By
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-white max-w-3xl leading-tight mb-10">
            Four convictions that shape every business decision
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {FOUNDER_PRINCIPLES.map((p, i) => (
              <div
                key={p.title}
                data-testid={`founder-principle-${i}`}
                className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6"
              >
                <div className="w-10 h-10 grid place-items-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25 mb-4">
                  <p.icon size={18} className="text-[#F0D788]" />
                </div>
                <h3 className="font-serif text-lg text-white mb-2">{p.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus areas */}
      <section data-testid="leadership-focus" className="py-14 md:py-18 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
              Leadership Focus
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">
              Strategic priorities under the MD&apos;s direct oversight
            </h2>
            <p className="mt-5 text-sm md:text-base text-white/70 leading-relaxed">
              Diganbhai personally champions the areas where customer outcomes and institutional
              integrity have the greatest impact.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FOUNDER_FOCUS.map((f, i) => (
              <div
                key={f}
                data-testid={`founder-focus-${i}`}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
              >
                <Award size={14} className="text-[#D4AF37] mt-1 shrink-0" />
                <div className="text-sm text-white/85">{f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder quote */}
      <section className="py-14 md:py-18 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-14">
            <Quote size={28} className="text-[#D4AF37] mb-6" />
            <div className="font-serif text-2xl md:text-3xl leading-snug text-white">
              "When we lend money, we don&apos;t just disburse a loan — we extend{" "}
              <span className="text-[#F0D788]">a hand of trust</span>. That hand has to stay
              steady for years, through every season."
            </div>
            <div className="mt-6 text-sm text-white/65 uppercase tracking-[0.22em]">
              — Digantkumar I. Gamit
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-18 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Want to partner with Sahyog Finance?
          </h2>
          <p className="text-base text-white/70 mb-7 max-w-2xl mx-auto">
            Whether you&apos;re a customer, a prospective DSA, an institutional investor or a
            community partner — Diganbhai and the leadership team are always open to a
            conversation.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/careers"
              data-testid="leadership-careers-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
            >
              Join the Partner Network <ArrowRight size={14} />
            </Link>
            <Link
              to="/investors"
              data-testid="leadership-investors-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm"
            >
              Investor Relations
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
